import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Smartphone, Share, PlusSquare, BellRing } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ChipSelect } from "@/components/Common";
import { api, errMsg } from "@/lib/api";
import { enablePush, disablePush, isIOS, isStandalone, pushSupported, pushPermission } from "@/lib/push";

const FREQ = [{ value: "instant", label: "Instantly" }, { value: "daily", label: "Daily digest" }, { value: "weekly", label: "Weekly digest" }];

const Row = ({ title, hint, children }) => (
  <div className="flex items-start justify-between gap-4 rounded-xl border border-slate-200 p-4"><div><div className="text-sm font-semibold text-slate-900">{title}</div>{hint && <div className="text-xs text-slate-500 mt-0.5">{hint}</div>}</div>{children}</div>
);

export const IOSHomeScreenTip = () => (
  <div className="rounded-xl bg-[#FAF8F2] border border-[#D4AF63]/40 p-4 text-sm text-slate-700 space-y-2" data-testid="ios-home-screen-tip">
    <div className="font-semibold text-[#003B2D] flex items-center gap-2"><Smartphone className="h-4 w-4" />Using an iPhone or iPad?</div>
    <p>Apple only allows push alerts for web apps saved to your Home Screen (iOS 16.4 or later):</p>
    <ol className="list-decimal pl-5 space-y-1">
      <li>Open Zunvel in Safari and tap <Share className="inline h-3.5 w-3.5" /> <b>Share</b>.</li>
      <li>Choose <PlusSquare className="inline h-3.5 w-3.5" /> <b>Add to Home Screen</b>, then <b>Add</b>.</li>
      <li>Open Zunvel from your Home Screen, sign in, and turn on push alerts here.</li>
    </ol>
  </div>
);

export function NotificationSettings({ onSaved }) {
  const { data, refetch } = useQuery({ queryKey: ["notif-prefs"], queryFn: () => api.get("/carematch/preferences").then((r) => r.data) });
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setP({ ...data, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || data.timezone }); }, [data]);
  if (!p) return null;
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const save = async (next = p) => {
    setBusy(true);
    try {
      await api.put("/carematch/preferences", { email_enabled: next.email_enabled, push_enabled: next.push_enabled, frequency: next.frequency, quiet_hours: next.quiet_hours, timezone: next.timezone });
      toast.success("Notification preferences saved"); refetch(); onSaved?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const togglePush = async (on) => {
    try {
      if (on) await enablePush(); else await disablePush().catch(() => {});
      const next = { ...p, push_enabled: on };
      setP(next); await save(next);
    } catch (e) { toast.error(e.message || errMsg(e)); }
  };
  const test = async () => { const { data: r } = await api.post("/push/test"); r.status === "sent" ? toast.success("Test push sent") : toast.error(r.error || "No device registered for push"); };
  const iosNeedsInstall = isIOS() && !isStandalone();
  return (
    <div className="space-y-3" data-testid="notification-settings">
      <Row title="In-app alerts" hint="Always on for opted-in CareMatch searches — shown in your notification bell."><span className="text-xs font-semibold text-teal-700">On</span></Row>
      <Row title="Email alerts" hint="Subject lines never include health details."><Switch checked={p.email_enabled} onCheckedChange={(v) => set("email_enabled", v)} data-testid="pref-email-switch" /></Row>
      <Row title="Push alerts (this device)" hint={pushSupported() ? `Lock-screen previews only say "You have a new CareMatch update." ${p.push_devices ? `· ${p.push_devices} device(s) registered` : ""}` : "Not supported in this browser."}>
        <Switch checked={p.push_enabled && pushPermission() === "granted"} disabled={!pushSupported()} onCheckedChange={togglePush} data-testid="pref-push-switch" />
      </Row>
      {iosNeedsInstall && <IOSHomeScreenTip />}
      {p.push_enabled && p.push_devices > 0 && <Button variant="ghost" size="sm" className="rounded-full text-teal-700" onClick={test} data-testid="pref-push-test-btn"><BellRing className="h-4 w-4 mr-1" />Send a test push</Button>}
      <div className="space-y-2"><Label>Email & push frequency</Label><ChipSelect single options={FREQ} value={p.frequency} onChange={(v) => set("frequency", v)} testid="pref-frequency" /></div>
      <div className="rounded-xl border border-slate-200 p-4 space-y-3">
        <div className="flex items-center justify-between"><div><div className="text-sm font-semibold">Quiet hours</div><div className="text-xs text-slate-500">Email & push wait until quiet hours end ({p.timezone}).</div></div>
          <Switch checked={p.quiet_hours.enabled} onCheckedChange={(v) => set("quiet_hours", { ...p.quiet_hours, enabled: v })} data-testid="pref-quiet-switch" /></div>
        {p.quiet_hours.enabled && <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">From</Label><Input type="time" value={p.quiet_hours.start} onChange={(e) => set("quiet_hours", { ...p.quiet_hours, start: e.target.value })} data-testid="pref-quiet-start" /></div>
          <div className="space-y-1"><Label className="text-xs">Until</Label><Input type="time" value={p.quiet_hours.end} onChange={(e) => set("quiet_hours", { ...p.quiet_hours, end: e.target.value })} data-testid="pref-quiet-end" /></div>
        </div>}
      </div>
      <Button className="rounded-full bg-[#003B2D] w-full" disabled={busy} onClick={() => save()} data-testid="pref-save-btn">{busy ? "Saving…" : "Save notification preferences"}</Button>
    </div>
  );
}

export function NotifyOptInDialog({ open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="notify-optin-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#003B2D]">Your CareMatch™ is saved</DialogTitle>
          <DialogDescription>Choose how Zunvel should tell you when matching care becomes available. You can change this any time.</DialogDescription>
        </DialogHeader>
        <NotificationSettings onSaved={onClose} />
        <Button variant="ghost" className="rounded-full" onClick={onClose} data-testid="notify-optin-skip-btn">In-app alerts only for now</Button>
      </DialogContent>
    </Dialog>
  );
}
